import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Shell from "@/components/Shell";
import Brandmark from "@/components/Brandmark";
import { SCOREAPP_SNAPSHOT_URL } from "@/lib/config";
import { setAttribution } from "@/lib/attribution";
import { track } from "@/lib/analytics";

// Cold-traffic orientation (search / Reddit / referral). Neutral, low-friction.
// Two choices: FAST (3-question State ID) or DEEPER (external ScoreApp Snapshot).
export default function Start() {
  const nav = useNavigate();

  useEffect(() => {
    setAttribution({ entry_context: "orientation" });
    track("orientation_view");
  }, []);

  const openSnapshot = () => {
    track("scoreapp_click", { source: "orientation" });
    window.open(SCOREAPP_SNAPSHOT_URL, "_blank", "noopener");
  };

  return (
    <Shell>
      <div className="min-h-[88vh] px-6 pt-20 pb-16 flex flex-col" data-testid="orientation">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
          <Brandmark size={34} />
        </motion.div>

        <div className="flex-1 flex flex-col justify-center py-6">
          <motion.h1
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif-display text-[27px] sm:text-3xl leading-[1.3] text-[#1A1A1A]"
          >
            Your performance isn't always the first thing to change.
          </motion.h1>
          <p className="mt-5 text-[15px] leading-relaxed text-[#474747]">
            Signal State ID helps you notice changes in how you're operating before they become
            obvious in decisions, reactions or performance.
          </p>
        </div>

        <motion.div
          className="mt-8 space-y-3"
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
        >
          <button
            data-testid="orientation-fast"
            onClick={() => nav("/navigate")}
            className="w-full flex items-center justify-between gap-3 bg-[#1A1A1A] text-[#FDFBF7] rounded-2xl px-5 py-4 ss-tap ss-focus hover:-translate-y-0.5"
          >
            <span className="flex flex-col text-left">
              <span className="text-[15px] font-semibold">Check my Signal State</span>
              <span className="text-[12px] text-[#FDFBF7]/70">3 questions · about 60 seconds</span>
            </span>
            <ArrowRight size={18} strokeWidth={1.8} />
          </button>

          <button
            data-testid="orientation-deeper"
            onClick={openSnapshot}
            className="w-full flex items-center justify-between gap-3 rounded-2xl border border-[#1A1A1A] px-5 py-4 ss-tap ss-focus"
          >
            <span className="flex flex-col text-left">
              <span className="text-[15px] font-semibold text-[#1A1A1A]">Take the Signal State Snapshot</span>
              <span className="text-[12px] text-[#6B6B6B]">7-9 questions · about 2 minutes</span>
            </span>
            <ArrowRight size={18} strokeWidth={1.8} className="text-[#8a6d10]" />
          </button>
          <p className="pt-1 text-center text-[12px] text-[#9a9a94]">
            The Snapshot is a one-time starting point. The 3-question check is what you'll use day to day.
          </p>
        </motion.div>
      </div>
    </Shell>
  );
}
